import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Mail } from "lucide-react";
import { socialApi, errorText } from "../../lib/member";

const HERE = "/friends/invite";
const signupTo = `/signup?redirect=${encodeURIComponent(HERE)}`;
const loginTo = `/login?redirect=${encodeURIComponent(HERE)}`;

// Warm, mobile-first invitation card in the dashboard 'paper' palette (the site header carries the script wordmark).
export function InviteShell({ children, testid }) {
  return (
    <div className="fs-themed min-h-screen" data-fs-theme="paper">
      <section className="px-5 pt-32 pb-16 sm:pt-40" data-testid={testid}>
        <div className="max-w-md mx-auto">
          <div className="fs-card px-6 py-8 sm:px-8 text-center" style={{ borderTop: "3px solid #B6A46D" }}>{children}</div>
        </div>
      </section>
    </div>
  );
}

const Eyebrow = ({ children }) => <p className="text-[11px] tracking-[0.25em] uppercase mb-3" style={{ color: "#8a7a45" }}>{children}</p>;
const Title = ({ children, testid }) => <h1 className="fs-serif mb-4" style={{ fontSize: "clamp(1.6rem, 6vw, 2rem)", lineHeight: 1.2 }} data-testid={testid}>{children}</h1>;
const Fine = ({ children, testid }) => <p className="text-xs fs-text-2 mt-5 leading-relaxed" data-testid={testid}>{children}</p>;

export function InviteWelcome() {
  return (
    <InviteShell testid="invite-welcome">
      <Eyebrow>A friend invitation</Eyebrow>
      <Title testid="invite-welcome-title">Someone would like to be friends with you</Title>
      <p className="text-[15px] leading-relaxed mb-6">American Farmstand is a members' community for discovering family farms, farmstands and country living. Sign in or create a free account to see who invited you and reply.</p>
      <p className="text-[15px] leading-relaxed mb-6" data-testid="invite-welcome-member">To enjoy friend profiles, messaging, and shared experiences together, <Link to="/subscribe" className="underline underline-offset-4" style={{ color: "#8a7a45" }} data-testid="invite-become-member">become a member</Link>.</p>
      <div className="flex flex-col gap-3">
        <Link to={signupTo} className="fs-btn justify-center" data-testid="invite-create-account">Create a free account</Link>
        <Link to={loginTo} className="fs-btn-2 justify-center" data-testid="invite-sign-in">Sign in</Link>
      </div>
      <Fine testid="invite-welcome-fine">Use the email address this invitation was sent to. Opening it, signing in or creating an account never accepts it; you'll choose Accept or Decline. Some features, like messages and friends' profiles, are part of a paid membership.</Fine>
    </InviteShell>
  );
}

export function InviteNotice({ text, signedIn, onSignOut }) {
  return (
    <InviteShell testid="invite-message">
      <Eyebrow>Friend invitation</Eyebrow>
      <p className="text-[15px] leading-relaxed" data-testid="invite-message-text">{text}</p>
      {signedIn && onSignOut && <Fine testid="invite-wrong-account">Signed in with a different email than the one invited? <button className="underline" onClick={onSignOut} data-testid="invite-switch-account">Sign out and use the invited address</button>.</Fine>}
      <Link to="/" className="fs-btn-2 mt-6 inline-flex" data-testid="invite-home">Visit American Farmstand</Link>
    </InviteShell>
  );
}

function ConfirmEmail({ onRecheck }) {
  const [state, setState] = useState(null);
  const send = () => socialApi.post("/email/verify/start").then(r => setState(r.status === "held_not_sent" ? "Email sending is turned off right now. Please try again later." : "Check your inbox for a confirmation link, then come back here.")).catch(e => setState(errorText(e)));
  return (
    <div data-testid="invite-verify">
      <p className="text-[15px] leading-relaxed mb-5">To reply, first confirm the email address on your account. This proves the invitation reached the right person.</p>
      <div className="flex flex-col gap-3">
        <button className="fs-btn justify-center" onClick={send} data-testid="invite-verify-send"><Mail className="w-4 h-4" /> Send confirmation email</button>
        <button className="fs-btn-2 justify-center" onClick={onRecheck} data-testid="invite-verify-recheck">I've confirmed, check again</button>
      </div>
      {state && <Fine testid="invite-verify-status">{state}</Fine>}
      <Fine>If this tab closes, open the invitation email again to come back.</Fine>
    </div>
  );
}

export function InviteAnswer({ info, token, onDone, onRecheck }) {
  const [busy, setBusy] = useState(false);
  const name = info.inviter.display_name;
  const act = (path, ok) => { setBusy(true); socialApi.post(path, { token }).then(ok).catch(e => onDone({ text: errorText(e) })).finally(() => setBusy(false)); };
  return (
    <InviteShell testid="invite-accept-card">
      <Eyebrow>A friend invitation</Eyebrow>
      <Title testid="invite-inviter">{name} invited you to be friends</Title>
      {!info.eligible ? <p className="text-[15px]" data-testid="invite-farm-account">Friend invitations are answered from a member account, not a farm account. Please sign in with your personal account.</p>
        : !info.email_verified ? <ConfirmEmail onRecheck={onRecheck} />
          : <>
            <p className="text-[15px] leading-relaxed mb-6">Friends on American Farmstand can share recipes, photos and plans with each other privately. You can remove a friend at any time.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="fs-btn justify-center" disabled={busy} onClick={() => act("/invites/accept", (r) => onDone({ accepted: r }))} data-testid="invite-accept"><Check className="w-4 h-4" /> Accept</button>
              <button className="fs-btn-2 justify-center" disabled={busy} onClick={() => act("/invites/decline", () => onDone({ text: "Invitation declined. It's now closed.", final: true }))} data-testid="invite-decline">Decline</button>
            </div>
            {!info.member && <Fine testid="invite-free-note">You can accept with a free account. Seeing friends' profiles and sending messages is part of an American Farmstand membership.</Fine>}
          </>}
      <Fine>This invitation expires on {new Date(info.expires_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}.</Fine>
    </InviteShell>
  );
}

export function InviteAccepted({ r }) {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <InviteShell testid="invite-accepted">
      <Eyebrow>You're connected</Eyebrow>
      <Title testid="invite-accepted-title">You and {r.friend.display_name} are now friends</Title>
      {r.member ? <button className="fs-btn justify-center w-full" onClick={() => navigate("/explore/friends")} data-testid="invite-go-friends">Go to Friends</button>
        : <>
          <p className="text-[15px] leading-relaxed mb-6" data-testid="invite-accepted-free">Your friendship is saved. With an American Farmstand membership you can see each other's shared profiles, photos and messages.</p>
          <div className="flex flex-col gap-3">
            <Link to="/subscribe" className="fs-btn justify-center" data-testid="invite-see-membership">See membership</Link>
            <Link to="/explore" className="fs-btn-2 justify-center" data-testid="invite-go-explore">Explore American Farmstand</Link>
          </div></>}
    </InviteShell>
  );
}
